/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createRegistrarClient, IRegistrarClient, IRegistrarClientOptions } from '@skype/tsregistrar';
import { calculateExpireTsInSec, calculateTtlInSec, fetchWithTimeout, Interval, Timeout, Timespan, toJson } from './Common';
import { ClientEventName, ConnectionTracker, Properties, ResponseData } from './ConnectionTracker';
import { CLIENT_VERSION, SUPPORTED_TOKEN_TYPES, USER_AUTHENTICATE_EVENT_NAME } from './Constants';
import { DisconnectReason } from './DisconnectReason';
import { ExponentialBackoff } from './ExponentialBackoff';
import {
    AuthTokenProvider,
    HttpHeaders, LogProvider, SendResponseResult, TimeoutOptions,
    TokenRequest,
    TokenResult,
    UserActivityState,
    IAudienceSubscriptionsResponse, IAudienceSubscriptionResponse, AudienceSubscriptionResult, IAudienceSubscription, TrouterState
} from './Interfaces';
import {
    AnonymousWebsocketAuthentication,
    IClientInfo, IMessageLossIndicator, ISocketIoOptions, ITrouterOptions, ITrouterRequest, ITrouterResponse,
    ServerState, UserActivityEventReason
} from './InternalInterfaces';
import { Logger } from './Logger';
import { adaptUrl, ensureNumber, isV4ConnectEvent, reconnectParamsWithoutUrls, redirectUrlIfPresent, usedProtocol } from './ProtocolAdapters';
import { FallbackReason, IWorker, TrouterFsm, State } from './TrouterFsm';
import { UserActivityObject, UserActivitySnapshot, AudienceSubscriptionState, AudienceSubscriptionSnapshot } from './TrouterManager';

// This is the current API version
const VERSION: string = 'v4';

// Default value for registration timeout
const DEFAULT_REGISTRATION_TIMEOUT_SEC = 3600;

// socket.io library is very much disconnected from the rest of the project
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare function require(arg: string): any;
// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-explicit-any
const io: any = require('./socket.io');

/**
 * Stub for IClientInfo
 */
class ClientInfo implements IClientInfo {
    public cv: string = CLIENT_VERSION;
    public ua: string = '';
    public hr: string = '';
    public v: string = '';
}

/**
 * Stub for ISocketIoOptions
 */
class SocketIoOptions implements ISocketIoOptions {
    public 'force new connection'?: boolean = true;
    public 'reconnect'?: boolean = false;
    public 'query'?: string = '';
    public ackTimeoutMs = 5000;
    public rewriteUrlForProxy(url: string) { return url; }
}

/**
 * Interface to work with SocketIO library
 */
interface ISocketIo {
    socketNumber: number;
    socket: { options: ISocketIoOptions };

    emit(event: string, handler?: () => void): void;
    emit(event: string, args: Record<string, unknown>, handler?: (name: string, args?: unknown) => void): void;
    // Some event arguments are from the socket.io library, others are directly coming from the server,
    // we currently don't type check them.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    on(event: string, handler: (...eventArgs: any[]) => void): void;
    disconnect(): void;
    send(msg: string): void;
    removeAllListeners(event: string): void;
}

/**
 * Params received during handshake
 */
export interface ITrouterConnectedEventArgs {
    // Time it took Trouter to perform the handshake request internally, in milliseconds. Number in quotes, e.g. "123"
    dur: string;
    // Connection valid TTL in seconds as returned by service.
    ttl: number;
}

/**
 * As there is no separate allocation request in /v4/c, all of the information comes as part of the trouter.connected event
 */
export type ITrouterConnectedEventArgsV4c = IAllocateResult & Omit<ITrouterConnectedEventArgs, 'ttl'> & { reconnectUrl: string };

/**
 * Params received during trouter.reconnect event
 */
export interface ITrouterReconnectEventArgs {
    // Reconnect target: self, host or global
    target: string;
    // Url of the deployment to reconnect to
    url?: string;
}

/**
 * Params received on message lost event
 */
interface ITrouterMessageLossEventArgs {
    droppedIndicators: IMessageLossIndicator[];
}

type TrouterRequestCallbackType = (val: string) => void;

const cvHeader: string = 'MS-CV';

/**
 * Request from downstream service
 */
class TrouterRequest implements ITrouterRequest {
    public startTS: number;
    public url: string;
    public shortUrl: string;
    public body: string;
    public headers: HttpHeaders;
    public id: number;
    public method: string;
    public replied: boolean;
    public timedout: boolean;
    public timeoutTimerId: Timeout | undefined;

    private dataCallback: TrouterRequestCallbackType | undefined;
    private cvCounter: number = 0;
    private receivedCv: string;

    public constructor(requestJson: string, private logger?: Logger) {
        const json: unknown = JSON.parse(requestJson);

        this.startTS = this.safeJsonNumber(json, 'startTS', 0);
        this.url = this.safeJsonString(json, 'url', '');
        this.shortUrl = this.safeJsonString(json, 'shortUrl', '');
        this.body = this.safeJsonString(json, 'body', '');
        this.headers = this.safeJsonRecord(json, 'headers', {});
        this.id = this.safeJsonNumber(json, 'id', -1);
        this.method = this.safeJsonString(json, 'method', '');

        this.replied = false;
        this.timedout = false;

        this.receivedCv = this.headers[cvHeader];
        this.updateCvHeader();
    }

    public get correlationVector(): string {
        return this.receivedCv ? `${this.receivedCv}.${this.cvCounter}` : '';
    }

    public on(target: string, targetCallback: (val?: string) => void) {
        if (target === 'data') {
            this.dataCallback = targetCallback;
        } else if (target === 'end') {
            if (typeof this.dataCallback === 'function') {
                this.dataCallback(this.body);
            }
            targetCallback();
        }
    }

    public incrementCorrelationVector() {
        ++this.cvCounter;
        this.updateCvHeader();
    }

    private updateCvHeader(): void {
        const cv: string = this.correlationVector;
        if (cv) {
            this.headers[cvHeader] = cv;
        }
    }

    private safeJsonNumber(json: unknown, property: string, defaultValue: number) {
        if (json !== null && json !== undefined && Object.prototype.hasOwnProperty.call(json, property)) {
            const jsonRecord = <Record<string, unknown>>json;
            if (typeof jsonRecord[property] === 'number') {
                return <number>(jsonRecord[property]);
            } else if (typeof jsonRecord[property] === 'string') {
                return parseFloat(<string>(jsonRecord[property]));
            } else {
                this.logger?.warn(`unexpected type of '${property}': ${typeof jsonRecord[property]}`);
            }
        }
        return defaultValue;
    }

    private safeJsonString(json: unknown, property: string, defaultValue: string) {
        if (json !== null && json !== undefined && Object.prototype.hasOwnProperty.call(json, property)) {
            const jsonRecord = <Record<string, unknown>>json;
            if (typeof jsonRecord[property] === 'string') {
                return <string>(jsonRecord[property]);
            } else {
                this.logger?.warn(`unexpected type of '${property}': ${typeof jsonRecord[property]}`);
            }
        }
        return defaultValue;
    }

    private safeJsonRecord(json: unknown, property: string, defaultValue: Record<string, string>) {
        if (json !== null && json !== undefined && Object.prototype.hasOwnProperty.call(json, property)) {
            const jsonRecord = <Record<string, unknown>>json;
            if (typeof jsonRecord[property] === 'object') {
                return <Record<string, string>>(jsonRecord[property]);
            } else {
                this.logger?.warn(`unexpected type of '${property}': ${typeof jsonRecord[property]}`);
            }
        }
        return defaultValue;
    }
}

/**
 * Response to downstream service
 */
class TrouterResponse implements ITrouterResponse {
    public constructor(
        private request: TrouterRequest,
        private responseData: ResponseData,
        private sendResponse: (request: TrouterRequest, responseData: ResponseData) => SendResponseResult) {
    }

    public writeHead(status: number, headers?: HttpHeaders) {
        this.responseData.status = status;
        this.responseData.headers = headers;
    }

    public write(chunk: string) {
        this.responseData.body += chunk;
    }

    public end(chunk?: string): SendResponseResult {
        if (chunk) {
            this.responseData.body += chunk;
        }

        return this.sendResponse(this.request, this.responseData);
    }
}

/**
 * An client-initiated event that is to be sent to trouter.
 */
class DownstreamEvent {
    public name: string;
    public args: Record<string, unknown>;
    public timeoutTimerId: Timeout;

    public constructor(name: string) {
        this.name = name;
        this.args = {};
        this.timeoutTimerId = <Timeout><unknown>0;
    }
}

/**
 * Reason why a connection switch is happening.
 */
export enum ReconnectReason {
    /** Configuration change coming from ECS, containing different URL */
    Configuration,

    /** In response to a `trouter.reconnect` socket.io event */
    ServerInitiated
}

/**
 * Interface to be implemented by Trouter consumer
 */
export interface IManagerConsumer {
    /** Called on received request */
    onDownstreamRequest(connection: TrouterConnection, request: ITrouterRequest, response: ITrouterResponse): void;
    /** Called once connection is established */
    onConnected(connection: TrouterConnection): void;
    /** Called once registration in EDF is succeed */
    onRegistered(connection: TrouterConnection): void;
    /** Called if registration in EDF fails or expires */
    onUnregistered(connection: TrouterConnection): void;
    /** Called once connection failed */
    onReconnecting(connection: TrouterConnection): void;
    /** Called when server asked to reconnect */
    onReconnectIsRequired(connection: TrouterConnection, useConnectParamsFromCache: boolean, reason: ReconnectReason): void;
    /** Called once connection is unregistered and closed */
    onDisconnected(connection: TrouterConnection): void;
    /** Called once connection is reaches the terminal state */
    onTerminalError(connection: TrouterConnection): void;
    /** Called when connection parameters are updated */
    onConnectionParametersUpdated(connectParams: Record<string, unknown>): void;
    /** Called when message loss indicator received */
    onTrouterMessageLost(flowTags: IMessageLossIndicator[]): void;
    /** Called once user activity state is accepted */
    onUserActivityStateAccepted(cv: string): void;
    /** Called when audience subscription set finishes */
    onAudiencesSetResolved(audienceSubscriptionsResponse: IAudienceSubscriptionsResponse, cv: string): void;
    /** Returns the current state of the connection state machine */
    getState(): TrouterState;
}

/**
 * Allocation result from Trouter server.
 * With /v4/a protocol, this corresponds to the data returned from the /v4/a HTTP request
 * With /v4/c protocol, this is returned as part of the `trouter.connected` event
 */
export interface IAllocateResult {
    url: string;
    surl: string;
    curlb: string;
    connectparams: Record<string, unknown>;
    socketio: string;
    id: string;
    ccid: string;
    ttl: string;
}

/**
 * Locally cached reconnection parameters
 *
 * Allows reconnecting to the same deployment and retaining the
 * allocated Connection ID, Trouter Endpoint URL, etc.
 * Consists of IAllocateResult.connectparams (opaque to the client)
 * extended with a bit more info about the deployment it came from.
 */
export type IReconnectParameters = {
    /** URL that was sent by the server during a previous connection, typically URL of a specific Trouter deployment */
    reconnectUrl?: string

    /** URL that was passed to the client in settings, typically a "go URL" (load balancer) */
    serviceUrl?: string

    /** connectparams / signature data */
    [param: string]: unknown
};

type PendingAudienceSubscription = {
    readonly audienceSetResolve: (response: IAudienceSubscriptionsResponse) => void;
    readonly timeoutId: Timeout;
}

/**
 * Trouter + registration flow implementation
 */
export class TrouterConnection implements IWorker {
    private readonly WEBSOCKET_TRANSPORT_NAME = 'websocket';
    private readonly XHR_POLLING_TRANSPORT_NAME = 'xhr-polling';

    private readonly AUDIENCE_SUBSCRIPTION_RESULT_ERROR = 'Error';
    private readonly AUDIENCE_SUBSCRIPTION_RESULT_UNSUBSCRIBED = 'Unsubscribed';
    private readonly AUDIENCE_SUBSCRIPTION_RESULT_TIMEOUT = 'Timeout';
    private readonly AUDIENCE_SUBSCRIPTION_RESULT_BAD_REQUEST = "BadRequest";

    private readonly AUDIENCE_SUBSCRIPTION_STATE_QUERY_PARAM_REGEX = /&audienceSubscriptionState=[^&]*|^audienceSubscriptionState=[^&]*&?/;

    private readonly logger: Logger;
    private readonly tokenBackoff: ExponentialBackoff;
    private readonly fsm: TrouterFsm;
    private readonly connectionTracker: ConnectionTracker;
    private readonly registrarClient?: IRegistrarClient;

    private connectionId = '';

    private inIncallMode = false;
    private timeoutOptions: TimeoutOptions;
    private connectionTimeoutId?: Timeout;
    private pingTimerId?: Interval;
    private pingResponseTimerId?: Timeout;
    private registrationTimerId?: Timeout;  // Used for three kinds of registration-related timers:
                                            // 1) TTL near expiration, 2) TTL fully expired, 3) retry after failure
    private incallModeTimerId?: Timeout;

    private socket?: ISocketIo;
    private allocateResult?: IAllocateResult;
    /** expiration's timestamp of allocated connectionID */
    private connectionExpireTimestampInSecs?: number;
    /** Seeded by externally cached connection parameters at startup, updated by each successful allocation result.
     *  The contents might be mangled in various ways (especially if coming from the external connection cache),
     *  so all code working with it must make no assumptions about which fields are actually present etc.
     */
    private reconnectParams?: IReconnectParameters;

    /** Allocated URL where library was able to successfully connect */
    private connectedUrl?: string;

    /** Name of currently used transport */
    private transportTypeName: string | undefined;

    private clientID: number;
    private connectionAttempt = 0;
    private connectedClientId = '';
    private domId: string | undefined;
    private isNavigatorOnline: boolean = true;
    private onNavigatorOnlineStatusUpdateBound = this.onNavigatorOnlineStatusUpdate.bind(this);  // Bound to allow removing the listener
    private c2cUrlBase: string = '';
    private connectingErrorsInRow = 0;

    /** Keeps track of 401s */
    private unauthorizedErrorCount = 0;

    private readonly clientInfo: Readonly<IClientInfo>;
    /** Used to calculate short URL in requests */
    private urlPath: string | undefined;
    private userActivityState: UserActivityObject;
    private pendingSentEventTimers: { [id: number]: string; } = {};

    private lastDisconnectReason: string = '';

    private readonly UNKNOWN_TRANSPORT = 'unknown_transport';
    private readonly connectingErrorsThreshold = 3;

    // this variable represents and ongoing audience subscription operation that has been initiated by user
    // if the operation value exists it means that user requested a subscription but there has not been an answer from server yet
    // valid only for longpoll 
    private pendingAudienceSubscription?: PendingAudienceSubscription = undefined;

    constructor(
        logProvider: LogProvider,
        private options: ITrouterOptions,
        private manager: IManagerConsumer,
        private tokenProvider: AuthTokenProvider,
        private usingLegacyTokenApi: boolean,
        initialUserActivityState: UserActivityObject,
        private protocolSelector: typeof usedProtocol,
        private audienceSubscriptionState?: AudienceSubscriptionState
    ) {
        this.logger = new Logger('Connection', logProvider);
        this.timeoutOptions = this.options.timeoutOptions;
        this.tokenBackoff = new ExponentialBackoff(this.logger, this.timeoutOptions.maxBackoffMs);
        this.clientID = Date.now();

        if ((typeof window !== 'undefined') && window.location) {
            this.domId = window.location.hostname;
        }

        const clientInfo = new ClientInfo();
        clientInfo.cv = CLIENT_VERSION;
        clientInfo.ua = '';

        if (this.options?.clientInfo) {
            clientInfo.ua = this.safeString(this.options.clientInfo.ua);
            clientInfo.v = this.safeString(this.options.clientInfo.v);
        }

        this.clientInfo = clientInfo;

        this.connectionTracker = new ConnectionTracker(
            logProvider,
            this.clientID,
            this.clientInfo,
            () => { return this.getServerState(); },
            this.options.endpointId,
            this.options.clientCorrelationID,
            this.options.environment
        );
        this.applyConnectionTrackerOptions(options);

        const useIncallMode = this.options.incallModeTimeoutMs > 0;
        this.fsm = new TrouterFsm(logProvider, this, useIncallMode, this.protocolSelector);

        if (options.registration) {
            const registrarOptions: IRegistrarClientOptions = {
                registrarUrl: options.registration.registrarUrl,
                proxyUrlRewrite: options.rewriteUrlForProxy,
                registrationId: options.registration.registrationId,
                requestTimeoutMs: options.timeoutOptions.fetchTimeoutMs,
                initialRetryDelayMs: 1000,
                maxRetryDelayMs: options.timeoutOptions.maxBackoffMs,
                usingLegacyTokenApi: this.usingLegacyTokenApi,
                maxRetriesForGetToken: options.retryLimitOnTokenFetch,
                extraRegistrationHeaders: options.extraConnectionHeaders
            };
            this.registrarClient = createRegistrarClient(logProvider, this.tokenProvider, registrarOptions);
        }

        this.userActivityState = initialUserActivityState;
    }

    public start(externalConnectParams?: IReconnectParameters) {
        this.logger.info('Starting');

        this.reconnectParams = externalConnectParams;

        if (typeof window !== 'undefined' && window.navigator && window.addEventListener) {
            this.isNavigatorOnline = window.navigator.onLine;
            window.addEventListener('online', this.onNavigatorOnlineStatusUpdateBound);
            window.addEventListener('offline', this.onNavigatorOnlineStatusUpdateBound);
            this.logger.debug(`Registered for browser online notifications - current state: ${this.isNavigatorOnline}`);
        } else {
            this.isNavigatorOnline = true;  // Assume always online if the API is not available
        }

        this.fsm.start();
    }

    public stop(dontUnregister: boolean) {
        this.logger.info('Stopping');

        if (typeof window !== 'undefined' && window.navigator) {
            window.removeEventListener('online', this.onNavigatorOnlineStatusUpdateBound);
            window.removeEventListener('offline', this.onNavigatorOnlineStatusUpdateBound);
        }

        this.fsm.stop(dontUnregister);
        this.connectionTracker.close();
    }

    public configure(options: ITrouterOptions) {
        // Currently only baseUrl and telemetrySettings options are supported for reconfiguration
        const urlChanged = this.options.trouterUrl !== options.trouterUrl;
        this.options = options;

        this.applyConnectionTrackerOptions(options);

        if (urlChanged) {
            this.logger.info('Configuration changed. Reconnection required.');
            this.fsm.onReconnectRequired(false, ReconnectReason.Configuration);
        }
    }

    public checkConnection(disconnectDetected: boolean) {
        this.logger.info(`checkConnection called with ${disconnectDetected}`);

        this.fsm.checkConnection(disconnectDetected);

        // Don't send on disconnectDetected === false, because Teams client calls it on timer
        if (disconnectDetected) {
            this.connectionTracker.sendTelemetry(ClientEventName.CheckConnection, {
                disconnectDetected: disconnectDetected
            }, []);
        }
    }

    public disableRegistrationsAndAutoReconnect() {
        this.stopRegistrationTimer();
        this.cancelPendingRegistrationRequests();
        this.fsm.disableAutoReconnect();
    }

    /** Returns info about connection */
    public getServerState(): ServerState {
        return new ServerState(
            this.connectionId,
            this.connectedClientId,
            this.domId ? this.domId : '',
            this.allocateResult ? this.allocateResult.url : '',
            this.allocateResult ? this.allocateResult.surl : '',
            this.c2cUrlBase,
            this.connectionExpireTimestampInSecs);
    }

    public getState(): State {
        return this.fsm.getState();
    }

    //////// Implement IWorker ////////

    public getToken(
        useCache: boolean,
        backoff: boolean,
        claimsChallenge: string | undefined,
        reason?: FallbackReason,
        retryCounter: number = 0
    ) {
        this.logger.info(`Getting token ${backoff ? 'with backoff' : 'without backoff'}`);

        const requestFunction = () => {
            this.connectionTracker.trackStart('token');
            const tokenRequest: TokenRequest = {
                needFresh: !useCache,
                wwwAuthenticateHeader: claimsChallenge,
                supportedTokenTypes: SUPPORTED_TOKEN_TYPES,
                purpose: 'trouter'
            };
            this.logger.info(
                `Requesting token: needFresh=${tokenRequest.needFresh} ` +
                `types=[${SUPPORTED_TOKEN_TYPES}], ` +
                `wwwAuthenticateHeader is ${tokenRequest.wwwAuthenticateHeader ? 'non empty' : 'empty'}`);

            this.tokenProvider(tokenRequest).then((token) => {
                this.logger.debug(`${token.tokenType} token is received`);
                this.connectionTracker.trackEnd('token');
                this.fsm.onTokenReceived(token, reason, this.reconnectParams);
            }).catch((error: Error) => {
                const msg = toJson(error.stack);
                this.logger.error(`Getting token failed, will retry after timeout. Error: ${msg}`);
                this.connectionTracker.trackError('token', msg);

                // Can't retry getToken request, which means client would not receive any notifications from trouter.
                // Unrecoverable error unless client tries to reconnect with Trouter
                //     - retryCounter tracks internal loop
                //     - unauthorizedErrorCount tracks external loop (consecutive number of sendAllocationFailures as a result of 401s)
                if (!this.canRetryTokenFetchRequest(retryCounter + this.unauthorizedErrorCount)) {
                    const errorMessage = 'getToken retry limit hit, reached terminal error state';
                    this.connectionTracker.trackError('token', errorMessage);
                    this.resetTokenBackoff();
                    this.fsm.onTerminalError();

                    return;
                }
                this.getToken(useCache, true, claimsChallenge, reason, retryCounter + 1);
            });
        };

        if (backoff) {
            this.tokenBackoff.backoff('getting token', requestFunction);
        } else {
            this.resetTokenBackoff();
            requestFunction();
        }
    }

    public startConnectionTimer() {
        this.stopConnectionTimer();
        this.logger.debug(`Starting connection timeout for ${this.timeoutOptions.connectionTimeoutMs} ms`);
        this.connectionTimeoutId = setTimeout(() => {
            this.logger.info('Connection timeout is fired');
            this.fsm.onConnectingTimeout();
        }, this.timeoutOptions.connectionTimeoutMs);
    }

    public stopConnectionTimer() {
        if (this.connectionTimeoutId) {
            this.logger.debug('Stopping connection timeout');
            clearTimeout(this.connectionTimeoutId);
            this.connectionTimeoutId = undefined;
        }
    }

    public startPingTimer() {
        if (this.transportTypeName === 'websocket') {
            this.logger.debug(`Starting ping timeout for ${this.timeoutOptions.pingTimeoutMs} ms`);
            this.pingTimerId = setInterval(() => {
                this.logger.info('Ping interval fired');
                this.fsm.onPingInterval();
            }, this.timeoutOptions.pingTimeoutMs);
        } else {
            this.logger.debug(`Not starting ping for transport ${this.transportTypeName}`);
        }
    }

    public stopPingTimer() {
        if (this.pingTimerId) {
            this.logger.debug('Stopping ping timeout');
            this.clearPingResponseTimer();
            clearInterval(this.pingTimerId);
            this.pingTimerId = undefined;
        }
    }

    public shouldSkipRegistration() {
        return this.options.registration === undefined;
    }

    public hasCustomRegistrationTtl() {
        return this.options.registration?.registrarTtlSec !== undefined && this.options.registration?.registrarTtlSec !== 0;
    }

    public startRegistrationTimer() {
        this.stopRegistrationTimer();

        const timeForReregistrationSec = 30;
        const [ttl, isTtlReduced] = this.getRegistrationTtl();

        if (ttl <= timeForReregistrationSec || !isTtlReduced) {
            this.logger.debug(`Starting registration expiration timer (TTL ${ttl} sec)`);

            this.registrationTimerId = setTimeout(() => {
                this.registrationTimerId = undefined;
                this.logger.warn('Registration expired but the connection is still alive. Should never happen');
                this.dispatchUnregistered();
            }, ttl * 1000);

            return;
        }

        const delay = ttl - timeForReregistrationSec;
        this.logger.debug(`Starting registration extension timer for ${delay} sec`);
        this.registrationTimerId = setTimeout(() => {
            this.logger.info('Registration extension timer fired');
            this.registrationTimerId = setTimeout(() => {
                this.registrationTimerId = undefined;
                this.logger.debug('Registration extension did not happen in time');
                this.dispatchUnregistered();
            }, timeForReregistrationSec * 1000);
            this.fsm.onRegistrationNearExpiry();
        }, delay * 1000);
    }

    public startRegistrationRetryTimer() {
        this.stopRegistrationTimer();

        // A simple static delay for now, per discussion in PR 870422.
        const registrationRetryDelaySec = 123;
        this.registrationTimerId = setTimeout(() => {
            this.registrationTimerId = undefined;
            this.fsm.onRetryRegistration();
        }, registrationRetryDelaySec * 1000);
    }

    public stopRegistrationTimer() {
        if (this.registrationTimerId) {
            this.logger.debug('Stopping registration timeout');
            clearTimeout(this.registrationTimerId);
            this.registrationTimerId = undefined;
        }
    }

    public resendRegistration(): Promise<void> {
        if (!this.registrarClient) {
            throw new Error('Trouter Client not configured to handle registrations');
        }

        this.fsm.onResendRegistration();

        return Promise.resolve();
    }

    public buildSocketIoUrlParams(userActivitySnapshot?: UserActivitySnapshot, audienceSubscriptionSnapshot?: AudienceSubscriptionSnapshot): string {
        if (!this.allocateResult) {
            throw new Error('Allocate result is undefined in buildSocketIoUrlParams()');
        }

        const urlParams: Record<string, string | number> = {};

        // Copy connection parameters (i.e. signature) from allocation response
        const signatureData = this.allocateResult.connectparams;
        for (const i of Object.keys(signatureData)) {
            if (signatureData[i] !== undefined) {
                const value = signatureData[i];
                if (typeof value === 'string' || typeof value === 'number') {
                    urlParams[i] = value;
                } else {
                    this.logger.error(`signatureData[${i}] has unsupported type ${typeof value}`);
                }
            }
        }

        // And add some more fields
        urlParams.v = VERSION;
        urlParams.tc = encodeURI(toJson(this.clientInfo));
        urlParams.timeout = this.timeoutOptions.pingTimeoutMs / 1000;
        urlParams.auth = 'true';

        if (this.options.endpointId) {
            urlParams.epid = this.options.endpointId;
        }

        if (userActivitySnapshot) {
            urlParams.userActivity = encodeURI(toJson(userActivitySnapshot));
        }

        if (audienceSubscriptionSnapshot) {
            urlParams.audienceSubscriptionState = encodeURIComponent(toJson(audienceSubscriptionSnapshot));
        }

        return this.appendConnectedClientIds(this.buildQuery(urlParams), true);
    }

    public startSocketIo(token: TokenResult) {
        this.logger.debug('Starting socket io');
        this.connectionTracker.trackStart('connectSocket');
        if (!this.allocateResult) {
            throw new Error('Allocate result is undefined in startSocketIo()');
        }

        const options: ISocketIoOptions = this.options.ioOptions ? { ...this.options.ioOptions } : new SocketIoOptions();
        // Take user activity snapshot (if a state isn't unknown) to make sure it's consistent between the function calls below.
        const userActivitySnapshot = this.userActivityState.state !== UserActivityState.Unknown
            ? this.userActivityState.increaseCvAndGetEventObject()
            : undefined;

        const audienceSubscriptionStateSnapshot = this.audienceSubscriptionState?.increaseCvAndGetEventObject();

        // Regardless of any passed in socket.io options, force the following settings.
        options['force new connection'] = true;
        options.reconnect = false;
        options.rewriteUrlForProxy = this.options.rewriteUrlForProxy;
        options.requestHeaders = {
            ...this.options.extraConnectionHeaders,
            ...headersFromToken(token, this.usingLegacyTokenApi)
        };

        // Query params to each socket.io request
        options.query = this.buildSocketIoUrlParams(userActivitySnapshot, audienceSubscriptionStateSnapshot);
        this.logger.info(`connecting to ${this.allocateResult.socketio}?${options.query}`);

        // msg868 - clear all events and disconnect
        this.stopSocketIo();

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-call
        this.socket = (this.options.io ?? io).connect(this.allocateResult.socketio, options);
        if (this.socket === undefined) {
            throw new Error('Can\'t create Socket.io object');
        }

        this.attachSocketIoHandlers(this.socket, token, userActivitySnapshot, audienceSubscriptionStateSnapshot);
    }

    public stopSocketIo() {
        if (this.socket) {
            this.logger.debug('clearing socket.io');
            try {
                for (const event of [
                    'connecting', 'connect', 'connect_failed', 'close_during_connecting', 'disconnect', 'reconnect', 'reconnect_failed',
                    'reconnecting', 'error', 'message', 'trouter.connected', 'trouter.reconnect', 'trouter.message_loss'
                ]) {
                    this.socket.removeAllListeners(event);
                }

                this.socket.disconnect();
                this.logger.debug('cleared socket');
                this.socket = undefined;
            } catch (error) {
                this.logger.error(`exception in disconnecting previous socket. Error: ${errorStackToJson(error)}`);
            }
        }
    }

    public dispatchConnected() {
        this.logger.info('dispatching connected');
        this.manager.onConnected(this);
    }

    public dispatchRegistered() {
        this.logger.info('dispatching registered');
        this.manager.onRegistered(this);
    }

    public dispatchUnregistered() {
        this.logger.info('dispatching unregistered');
        this.manager.onUnregistered(this);
    }

    public dispatchDownstreamRequest(request: ITrouterRequest) {
        this.logger.debug('dispatching downstream request');

        try {
            const response = new TrouterResponse(<TrouterRequest>request, new ResponseData(request.id),
                (req: TrouterRequest, responseData: ResponseData) => {
                    this.logger.debug('sending response to downstream');

                    return this.sendResponse(req, responseData);
                });
            this.manager.onDownstreamRequest(this, request, response);
        } catch (error) {
            this.logger.error(`exception in socket.on message. Error : ${errorStackToJson(error)}`);
        }
    }

    public dispatchReconnecting() {
        this.logger.info('dispatching reconnecting');
        this.manager.onReconnecting(this);
    }

    public dispatchReconnectIsRequired(useConnectParamsFromCache: boolean, reason: ReconnectReason) {
        this.logger.info('dispatching reconnect is required by server');
        this.manager.onReconnectIsRequired(this, useConnectParamsFromCache, reason);
    }

    public dispatchDisconnected() {
        this.logger.info('dispatching disconnected');
        this.manager.onDisconnected(this);
    }

    public dispatchTerminalError() {
        this.logger.info('dispatching terminal error');
        this.manager.onTerminalError(this);
    }

    public dispatchTrouterMessageLost(messageLossIndicators: IMessageLossIndicator[]) {
        this.logger.info('dispatching trouter message lost');
        this.manager.onTrouterMessageLost(messageLossIndicators);
    }

    public countDisconnectBeforeConnectionEstablishment() {
        this.logger.warn('counting disconnect before connection was fully established as a connection failure');
        this.connectingErrorsInRow++;
        if (this.connectingErrorsInRow >= this.connectingErrorsThreshold) {
            this.resetReconnectParamsOnErrorThreshold();
        }
    }

    public sendProcessedDroppedIndicators(messageLossIndicators: IMessageLossIndicator[]) {
        try {
            this.logger.debug('emitting processed flow tags to the server');
            const processedMessageLossEvent = new DownstreamEvent('trouter.processed_message_loss');
            processedMessageLossEvent.args = { droppedIndicators: messageLossIndicators };
            this.sendDownstreamEvent(processedMessageLossEvent, () => {
                this.logger.info('emitted processed flow tags to the server');
            });
        } catch (error) {
            const msg = errorStackToJson(error);
            this.logger.error(`unable to send processed message loss event. Error: ${msg}`);
            this.connectionTracker.trackError('trouter.processed_message_loss', msg, false);
        }
    }

    public sendAllocateRequest(token: TokenResult) {
        this.connectionAttempt++;
        this.connectionTracker.trackNewConnection();

        let url = this.options.trouterUrl;
        let connectParams: Record<string, unknown> | null;

        let rp = this.reconnectParams;
        const expiration = typeof rp?.se === 'string' ? parseInt(rp.se, 10) : (typeof rp?.se === 'number' ? rp.se : undefined);
        if (expiration && expiration <= Date.now() + 3600000) {  // 1 hour
            this.logger.warn(`Dropping expired cached connection parameters: ${new Date(expiration)}`);
            this.reconnectParams = rp = undefined;
        }
        if (rp && rp.serviceUrl !== url) {
            this.logger.warn(`Dropping cached connection parameters for a different environment (${rp.serviceUrl}, now ${url})`);
            this.reconnectParams = rp = undefined;
        }
        if (rp?.reconnectUrl) {
            url = rp.reconnectUrl;
        }
        if (rp) {
            // Do not append serviceUrl and reconnectUrl as query parameters
            connectParams = { ...rp, serviceUrl: undefined, reconnectUrl: undefined };
        } else {
            connectParams = null;
        }

        url = adaptUrl(url, 'v4a');
        url = this.appendCorrelationIds(url, false);
        url = this.appendEndpointId(url, false);
        if (connectParams) {
            url += `&${this.buildQuery(connectParams)}`;
            // Currently server doesn't return "v" in allocate result, but it
            // can be changed, so implement it this way just in case
            if (!connectParams.v) {
                url += `&v=${VERSION}`;
            }
        }

        url = this.options.rewriteUrlForProxy(url);

        const allocateRequest = new Request(url, {
            method: 'POST',
            mode: 'cors',
            headers: new Headers({
                'Content-Type': 'text/plain',
                ...this.options.extraConnectionHeaders,
                ...headersFromToken(token, this.usingLegacyTokenApi),
            })
        });

        this.logger.info(`sendAllocateRequest: POST ${url}`);
        this.connectionTracker.trackStart('allocation');
        const startTimestamp = Date.now();
        let responseStatusCode = -1;
        let responseClaimsChallenge: string | undefined;
        let responseSkypetokenDeprecated = false;
        fetchWithTimeout(allocateRequest, this.timeoutOptions.fetchTimeoutMs).then((response: Response) => {
            responseStatusCode = response.status;
            if (!response.ok) {
                responseClaimsChallenge = response.headers.get('www-authenticate') ?? undefined;
                responseSkypetokenDeprecated = response.headers.get('x-trouter-skypetoken-deprecated') === '1';
                this.logger.warn(`Allocation request got response status ${response.status}, www-authenticate header was ${responseClaimsChallenge ? 'not empty' : 'empty'}`);
                throw new Error(response.statusText);
            }

            const contentType = response.headers.get('content-type');
            if (!contentType || (contentType !== 'application/json' && !contentType.startsWith('application/json;'))) {
                throw new Error(`Content-type '${contentType}' is unexpected`);
            }
            this.connectionTracker.trackEnd('allocation');

            return response.json();
        }).then((responseObj) => {
            this.unauthorizedErrorCount = 0;
            // We perform no parsing/validation of the response received from server
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.onAllocationResponse(responseObj, token);
        }).catch((error: Error) => {
            this.connectingErrorsInRow++;
            const msg = `${error}${responseStatusCode >= 0 ? `, status code ${responseStatusCode}` : ''}`;
            this.logger.error(`${this.connectingErrorsInRow} failed connecting attempt(s) in a row. ${msg}`);
            this.connectionTracker.trackError('allocation', msg);

            if (responseSkypetokenDeprecated) {
                const errorMessage = 'Skypetoken deprecated response, not retrying any further';
                this.logger.error(errorMessage);
                this.connectionTracker.trackError('allocation', errorMessage);
                this.fsm.onTerminalError();

                return;
            }
            if (responseStatusCode === 401) {
                this.unauthorizedErrorCount++;
            }
            if (!this.canRetryTokenFetchRequest(this.unauthorizedErrorCount)) {
                const errorMessage = 'getToken retry limit hit, reached terminal error state';
                this.connectionTracker.trackError('allocation', errorMessage);
                this.fsm.onTerminalError();

                return;
            }

            if (responseStatusCode === -1 && !this.isNavigatorOnline) {
                this.logger.info('Expected failure, the browser says it is not online at the moment');
            } else if (this.reconnectParams && this.connectingErrorsInRow >= this.connectingErrorsThreshold) {
                if (responseStatusCode >= 400 && responseStatusCode <= 599) {
                    this.resetReconnectParamsOnErrorThreshold();
                } else if (this.reconnectParams.reconnectUrl && this.connectingErrorsInRow % 3 === 0) {
                    this.logger.warn(`${this.connectingErrorsInRow} connection attempts, testing nominal service URL`);
                    // Make sure to finish the check before connection timeout passes to avoid unexpected FSM calls
                    const timeoutMs = Math.min(
                        this.timeoutOptions.connectionTimeoutMs - (Date.now() - startTimestamp) - 500,
                        this.timeoutOptions.fetchTimeoutMs);
                    this.testNominalUrlConnectivity(timeoutMs)
                        .then(result => {
                            this.connectionTracker.trackProgress('nomcheck', result ? 'ok' : 'failed');
                            if (result) {
                                this.logger.warn('Nominal service URL is reachable, erasing cached reconnect URL');
                                if (this.reconnectParams) {
                                    delete this.reconnectParams.reconnectUrl;
                                }
                            } else {
                                this.logger.warn('Nominal service URL is not reachable either, keeping cached reconnect URL');
                            }

                            this.fsm.onAllocationFailed(false, undefined);
                        }, () => {
                            // Should not happen, but removes no-floating-promises warning
                            this.fsm.onAllocationFailed(false, undefined);
                        });

                    return;
                }
            }

            this.fsm.onAllocationFailed(responseStatusCode === 401, responseClaimsChallenge);
        });
    }

    public testNominalUrlConnectivity(timeoutMs: number): Promise<boolean> {
        if (timeoutMs < 1000) {
            this.logger.warn(`There is no time left to reasonably perform the nominal service URL connectivity check (${timeoutMs} ms), falling back to assuming that the connectivity is fine`);

            return Promise.resolve(true);
        }

        let testRequest: Request;
        try {
            const url = new URL(this.options.trouterUrl);
            // Modify the URL to reach the root page (which returns a plain text string "Trouter")
            url.pathname = '/';
            // Add a few query parameters for possible classification and troubleshooting
            url.search = '?' + this.buildQuery({
                check: Date.now(),
                cor_id: encodeURIComponent(this.options.clientCorrelationID),
                epid: encodeURIComponent(this.options.endpointId ? this.options.endpointId : ''),
                tc: encodeURIComponent(toJson(this.clientInfo))
            });
            testRequest = new Request(this.options.rewriteUrlForProxy(url.toString()), {
                method: 'GET',
                headers: { Accept: 'text/plain' }
            });
        } catch (error) {
            this.logger.warn(`Nominal service URL connectivity test request could not be created (${error}), falling back to assuming that the connectivity is fine`);

            return Promise.resolve(true);
        }

        return fetchWithTimeout(testRequest, timeoutMs)
            .then((response: Response) => {
                if (response.status !== 200) {
                    throw new Error(`Not 200 OK: ${response.status} ${response.statusText}`);
                }

                return response.text();
            })
            .then((body: string) => {
                if (body !== 'Trouter') {
                    throw new Error(`Not "Trouter": ${body.substring(0, 16)}${body.length > 16 ? '...' : ''}`);
                }

                return true;
            })
            .catch((error) => {
                this.logger.error(`Nominal service URL connectivity test failed: ${error}`);

                return false;
            });
    }

    public sendPingRequest() {
        if (this.socket && this.pingResponseTimerId === undefined) {
            try {
                this.logger.debug('emitting ping event');
                let timeoutReached = false;
                this.socket.emit('ping', () => {
                    if (timeoutReached === true) {
                        return;
                    }
                    this.onPingResponse();
                });
                this.pingResponseTimerId = setTimeout(() => {
                    this.logger.error('Ping response timeout is fired');
                    timeoutReached = true;
                    this.clearPingResponseTimer();
                    this.fsm.onPingResponseTimeout();
                }, this.timeoutOptions.pongTimeoutMs);
            } catch (error) {
                const msg = errorStackToJson(error);
                this.logger.error(`unable to send ping. Error: ${msg}`);
                this.connectionTracker.trackError('ping', msg, false);
            }
        }
    }

    public connectV4c(token: TokenResult, reason?: FallbackReason) {
        const options: ISocketIoOptions = this.options.ioOptions ? { ...this.options.ioOptions } : new SocketIoOptions();

        // Regardless of any passed in socket.io options, force the following settings.
        options['force new connection'] = true;
        options.reconnect = false;

        if (
            this.reconnectParams?.serviceUrl &&
            // previous input URL != current input URL (passed via `start()`)
            adaptUrl(this.reconnectParams.serviceUrl, 'v4c') !== adaptUrl(this.options.trouterUrl, 'v4c')
        ) {
            this.logger.warn(`Dropping cached connection parameters for a different environment (${this.reconnectParams.serviceUrl}, now ${this.options.trouterUrl})`);
            this.reconnectParams = undefined;
        }

        const inputUrl =
            redirectUrlIfPresent(reason) ??
            (reason?.kind !== 'redirect-no-host' ? this.reconnectParams?.reconnectUrl : undefined) ??
            this.options.trouterUrl;

        const url = adaptUrl(inputUrl, 'v4c');

        options['skipped handshake data'] = {
            timeout: 70,
            websocketUrl: url
        };

        options.query = this.buildV4cUrlParams();
        options.rewriteUrlForProxy = this.options.rewriteUrlForProxy;

        try {
            this.stopSocketIo();

            this.transportTypeName = 'websocket';
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-assignment
            this.socket = (this.options.io ?? io).connect(this.options.trouterUrl, options);
            if (this.socket === undefined) {
                throw new Error('failed to create Socket.io object');
            }

            const audienceSubscriptionStateSnapshot = this.audienceSubscriptionState?.increaseCvAndGetEventObject();

            this.attachSocketIoHandlers(this.socket, token, undefined, audienceSubscriptionStateSnapshot);
        } catch (e: unknown) {
            this.logger.error(`${e}`);
            this.connectionTracker.trackError('v4c', `${e}`);
            this.fsm.onV4cException();
        }
    }

    public sendV4cAuthenticationEvent(token: TokenResult): void {
        const payload: AnonymousWebsocketAuthentication = {
            headers: {
                ...this.options.extraConnectionHeaders,
                ...headersFromToken(token, this.usingLegacyTokenApi)
            },
            connectparams: reconnectParamsWithoutUrls(this.reconnectParams)
        };
        this.socket?.emit(USER_AUTHENTICATE_EVENT_NAME, payload);
    }

    public setUserActivityState(userActivityObject: UserActivityObject) {
        const changed: boolean = (userActivityObject.state !== this.userActivityState.state);
        this.userActivityState = userActivityObject;
        if(changed) {
            this.logger.info(`Changing user activity state to '${userActivityObject.toEventJSON()}'`);
            this.fsm.onSetNewUserActivityState();
        } else {
            this.logger.debug(`Not changing the same user activity state '${userActivityObject.toEventJSON()}'`);
            this.manager.onUserActivityStateAccepted(userActivityObject.correlationVector.value());
        }
    }

    public sendUserActivityState(reason: UserActivityEventReason, connected: boolean) {
        if (this.userActivityState.state === UserActivityState.Unknown) {
            return;
        }
        if (this.transportTypeName === 'websocket' && connected) {
            if (reason === UserActivityEventReason.Connected) {
                this.sendUserActivityStateMultiple(2);
            } else {
                this.sendUserActivityStateMultiple(1);
            }
        } else if (this.transportTypeName === 'xhr-polling') { // ignoring the connected flag
            if (reason === UserActivityEventReason.Modified) {
                this.fsm.forceReconnect('user activity/force reconnect');
            }
        }
    }

    public setAudienceSubscriptionsAsync(audienceSubscriptionState: AudienceSubscriptionState, timeoutInMs: number): Promise<IAudienceSubscriptionsResponse> {
        const previousAudienceSubscriptionState = this.audienceSubscriptionState;
        this.audienceSubscriptionState = audienceSubscriptionState;

        if (this.transportTypeName === this.WEBSOCKET_TRANSPORT_NAME) {
            return this.setAudienceSubscriptionsInternalAsync(audienceSubscriptionState.increaseCvAndGetEventObject(), timeoutInMs);
        }

        if (this.transportTypeName === this.XHR_POLLING_TRANSPORT_NAME) {
            const setAudienceSubscriptionsPromise = this.setAudienceSubscriptionsLongpollInternalAsync(audienceSubscriptionState.increaseCvAndGetEventObject(), timeoutInMs, previousAudienceSubscriptionState);
            this.fsm.forceReconnect('set audience subscription force reconnect');
            return setAudienceSubscriptionsPromise;
        }

        throw new Error('set audience subscription executed on an unknown transport');
    }

    private setAudienceSubscriptionsUnsafeAsync(audienceSubscriptionSnapshot?: AudienceSubscriptionSnapshot): Promise<IAudienceSubscriptionsResponse> | undefined {
        if (!audienceSubscriptionSnapshot) {
            return undefined;
        }

        if (this.transportTypeName === this.WEBSOCKET_TRANSPORT_NAME) {
            return this.setAudienceSubscriptionsInternalAsync(audienceSubscriptionSnapshot, 15000);
        }
    }

    private async setAudienceSubscriptionsInternalAsync(audienceSubscriptionSnapshot: AudienceSubscriptionSnapshot, timeoutInMs: number): Promise<IAudienceSubscriptionsResponse> {
        this.logger.info('[WebSocket] Audience subscription set requested.');

        const audienceSubscriptionEvent = new DownstreamEvent('audience.subscribe');
        audienceSubscriptionEvent.args = audienceSubscriptionSnapshot;

        const result = await new Promise((resolve: (response: IAudienceSubscriptionsResponse) => void) => {
            let hasTimedOut = false;

            const timeoutId = setTimeout(() => {
                hasTimedOut = true;
                return resolve(this.buildAudienceSubscriptionsTimeoutResponse(audienceSubscriptionSnapshot));
            }, timeoutInMs);

            this.sendDownstreamEvent(audienceSubscriptionEvent, (_: string, args?: unknown) => {
                if (hasTimedOut) {
                    return;
                }

                clearTimeout(timeoutId);
                this.logger.debug(`[Websocket] Audience subscription response: ${args}`);

                if (this.pendingAudienceSubscription) {
                    this.onAudienceSubscriptionResult(args as IAudienceSubscriptionsResponse);
                }

                resolve(args as IAudienceSubscriptionsResponse);
            });
        });

        this.manager.onAudiencesSetResolved(result, audienceSubscriptionSnapshot.cv);
        return result;
    }

    private async setAudienceSubscriptionsLongpollInternalAsync(audienceSubscriptionSnapshot: AudienceSubscriptionSnapshot, timeoutInMs: number, previousSubscriptionState?: AudienceSubscriptionState): Promise<IAudienceSubscriptionsResponse> {
        this.logger.info('[XHR Polling] Audience subscription set requested.');

        if (this.pendingAudienceSubscription) {
            this.logger.error('Racing audience subscriptions occured. This situation resolves into undefined scenario and/or nasal demons.');
            clearTimeout(this.pendingAudienceSubscription.timeoutId);
        }

        if (audienceSubscriptionSnapshot.audiences.length === 0) {
            return this.handleAudienceUnsubscribeLongpoll(audienceSubscriptionSnapshot.cv, previousSubscriptionState);
        }

        const result = await new Promise((resolve: (response: IAudienceSubscriptionsResponse) => void) => {
            const timeoutId = setTimeout(() => {
                this.pendingAudienceSubscription = undefined;
                return resolve(this.buildAudienceSubscriptionsTimeoutResponse(audienceSubscriptionSnapshot));
            }, timeoutInMs);

            this.pendingAudienceSubscription = {
                audienceSetResolve: resolve,
                timeoutId
            };
        });

        if (this.manager.getState() === TrouterState.Unknown || this.transportTypeName === this.WEBSOCKET_TRANSPORT_NAME) {
            return result;
        }

        const currentResponse = result.responses[0];
        if (currentResponse?.result.audienceSubscriptionState === this.AUDIENCE_SUBSCRIPTION_RESULT_BAD_REQUEST) {
            this.clearAudienceSubscriptionStateQueryParam();

            this.manager.onAudiencesSetResolved(result, audienceSubscriptionSnapshot.cv);
            return result;
        }

        const currentAudienceResponse = result.responses.find(r => r.audienceId === audienceSubscriptionSnapshot.audiences[0].id);
        if (currentAudienceResponse?.result.audienceSubscriptionState === this.AUDIENCE_SUBSCRIPTION_RESULT_ERROR) {
            this.clearAudienceSubscriptionStateQueryParam();

            this.manager.onAudiencesSetResolved(result, audienceSubscriptionSnapshot.cv);
            return result;
        }

        // only notify user abount un-subscription if the target audience actually changed
        const subscribesToDifferentAudience = previousSubscriptionState?.audienceSubscriptionModel.audienceSubscriptions[0]?.id !== currentResponse?.audienceId;
        if (previousSubscriptionState && subscribesToDifferentAudience) {
            const synthResponses = this.mapToSyntheticAudienceSubscriptionResponses(previousSubscriptionState.audienceSubscriptionModel.audienceSubscriptions, this.AUDIENCE_SUBSCRIPTION_RESULT_UNSUBSCRIBED, 200);
            result.responses.push(...synthResponses);
        }

        this.manager.onAudiencesSetResolved(result, audienceSubscriptionSnapshot.cv);
        return result;
    }

    // remove the `audienceSubscriptionState` param from longpoll query string so the subscription is not attempted over and over creating loop
    private clearAudienceSubscriptionStateQueryParam() {
        if (this.socket?.socket?.options?.query) {
            this.socket.socket.options.query = this.socket.socket.options.query.replace(this.AUDIENCE_SUBSCRIPTION_STATE_QUERY_PARAM_REGEX, '');
        }
    }

    private handleAudienceUnsubscribeLongpoll(correlationVector: string, previousSubscriptionState?: AudienceSubscriptionState): IAudienceSubscriptionsResponse {
        this.audienceSubscriptionState = undefined;

        const result = {
            responses: []
        } as IAudienceSubscriptionsResponse;

        if (previousSubscriptionState) {
            const synthResponses = this.mapToSyntheticAudienceSubscriptionResponses(previousSubscriptionState.audienceSubscriptionModel.audienceSubscriptions, this.AUDIENCE_SUBSCRIPTION_RESULT_UNSUBSCRIBED, 200);
            result.responses.push(...synthResponses);
        }

        this.manager.onAudiencesSetResolved(result, correlationVector);
        return result;
    }

    private buildAudienceSubscriptionsTimeoutResponse(audienceSubscriptionSnapshot: AudienceSubscriptionSnapshot) {
        this.logger.error("Audience subscription attempt has timed out.");

        return {
            responses: this.mapToSyntheticAudienceSubscriptionResponses(audienceSubscriptionSnapshot.audiences, this.AUDIENCE_SUBSCRIPTION_RESULT_TIMEOUT)
        } as IAudienceSubscriptionsResponse;
    }

    private mapToSyntheticAudienceSubscriptionResponses(audiences: IAudienceSubscription[], synthState: string, synthResponseStatus?: number): IAudienceSubscriptionResponse[] {
        return audiences
            .map(audSub => ({
                audienceId: audSub.id,
                result: {
                    audienceSubscriptionState: synthState,
                    responseStatus: synthResponseStatus
                } as AudienceSubscriptionResult
            } as IAudienceSubscriptionResponse));
    }

    public expediteBackoff() {
        this.tokenBackoff.expediteIfPending();
    }

    public sendRegisterRequest() {
        if (!this.options.registration || !this.registrarClient) {
            throw new Error('Internal error - options.registration is undefined');
        }
        if (!this.allocateResult) {
            throw new Error('Allocate result is undefined in sendRegisterRequest()');
        }

        this.logger.info('sending register request');
        const span = new Timespan();
        this.connectionTracker.trackStart('registration');
        const [registrationTtl,] = this.getRegistrationTtl();
        this.registrarClient.register({
            appId: this.options.registration.pnhAppId,
            aesKey: '',
            languageId: 'en-US',
            platform: this.options.registration.platform,
            templateKey: this.options.registration.pnhTemplateKey,
            platformUIVersion: this.options.registration.platformUIVersion,
            productContext: this.options.registration.productContext
        }, {
            TROUTER: [{
                context: this.options.registration.context,
                path: this.allocateResult.surl,
                ttl: registrationTtl
            }]
        }).then(() => {
            this.logger.info('Register request successful');
            this.connectionTracker.trackEnd('registration');
            this.fsm.onRegistrationSucceeded();
            this.connectionTracker.sendTelemetry(ClientEventName.Registration, {
                duration: span.duration
            }, []);
        }).catch(error => {
            this.logger.error(`Register request failed. Error: ${error}`);
            this.connectionTracker.trackError('registration', errorMessageToString(error));
            this.fsm.onRegistrationFailed();
            this.connectionTracker.sendTelemetry(ClientEventName.Registration, {
                duration: span.duration
            }, []);
        });
    }

    public sendUnregisterRequest() {
        this.logger.info('sending unregister request');
        const span = new Timespan();
        if (!this.options.registration || !this.registrarClient) {
            throw new Error('Internal error - options.registration is undefined');
        }

        this.connectionTracker.trackStart('unregistration');
        this.registrarClient.unregister().then(() => {
            this.logger.info('Unregister request successful');
            this.connectionTracker.trackEnd('unregistration');
            this.fsm.onUnregistrationDone();
            this.connectionTracker.sendTelemetry(ClientEventName.Unregistration, {
                duration: span.duration
            }, []);
        }).catch(error => {
            this.logger.error(`Unregister request failed. Error: ${error}`);
            this.connectionTracker.trackError('unregistration', errorMessageToString(error));

            // The Registrar Client library already does internal retries on 5xx HTTP responses etc.
            // So if we end up here, something more exceptional must have happened. There is little
            // point delaying the stopping further by trying again, just finish as if successful.
            this.fsm.onUnregistrationDone();

            this.connectionTracker.sendTelemetry(ClientEventName.Unregistration, {
                duration: span.duration
            }, []);
        });
    }

    public resetTokenBackoff() {
        this.tokenBackoff.reset();
    }

    public cancelPendingRegistrationRequests() {
        if (this.registrarClient) {
            this.registrarClient.cancelPendingRequests();
        }
    }

    public clearSentEventTimers() {
        const keys = Object.keys(this.pendingSentEventTimers);
        if (keys.length > 0) {
            this.logger.debug('Clearing all pending downstream events related timers');
            for (const key of keys) {
                this.clearSentEventTimer(<Timeout><unknown>Number(key));
            }
        }
    }

    public restartIncallModeTimer() {
        this.clearIncallModeTimerId();
        this.logger.debug('Restarting incall mode timer');
        this.incallModeTimerId = setTimeout(() => {
            this.logger.info('Call mode timer fired');
            this.fsm.onIncallModeTimer();
        }, this.options.incallModeTimeoutMs);
    }

    public enterIncallMode() {
        this.logger.info('Entering incall mode');
        this.timeoutOptions = this.options.incallTimeoutOptions;
        this.tokenBackoff.setMaxBackoffMs(this.timeoutOptions.maxBackoffMs);
        // Uncomment to enable change of timeout in XHR mode - do it once server start to support it
        // if (this.socket) {
        //     this.socket.socket.options.query = this.buildSocketIoUrlParams();
        // }
        this.inIncallMode = true;
    }

    public exitIncallMode() {
        this.logger.info('Exiting incall mode');
        this.clearIncallModeTimerId();
        this.timeoutOptions = this.options.timeoutOptions;
        this.tokenBackoff.setMaxBackoffMs(this.timeoutOptions.maxBackoffMs);
        // Uncomment to enable change of timeout in XHR mode - do it once server start to support it
        // if (this.socket) {
        //     this.socket.socket.options.query = this.buildSocketIoUrlParams();
        // }
        this.inIncallMode = false;
    }

    public isIncallMode() {
        return this.inIncallMode;
    }

    public sendDisconnectTelemetryEvent(reason?: string) {
        const props = <Properties>{ reason: reason, serverClosed: !this.fsm.isActive() };
        this.connectionTracker.trackDisconnected(props);
        this.connectionTracker.clearConnectedInfo();
    }

    public forceReconnectDueToNoRegistration() {
        this.fsm.forceReconnect('force reconnect due to no registration');
    }

    private resetReconnectParamsOnErrorThreshold() {
        this.logger.warn(`${this.connectingErrorsInRow} connection attempts, server-side failure: erasing cached connection parameters`);
        this.reconnectParams = undefined;
    }

    ////////// SocketIO callbacks //////////
    private onSocketConnecting(transportTypeName: string) {
        this.logger.info(`onSocketConnecting(${transportTypeName})`);
        this.transportTypeName = transportTypeName;
        this.connectionTracker.trackProgress('connecting', this.transportTypeName);
        this.fsm.onConnecting();
    }
    private onSocketConnect(token: TokenResult) {
        this.logger.info('onSocketConnect');
        this.fsm.onSocketConnect(token);
    }
    private onSocketConnectFailed(reason: string): void {
        this.logger.error('onSocketConnectFailed');
        this.connectionTracker.trackError(
            'connect_failed', reason, true, this.transportTypeName ? this.transportTypeName : this.UNKNOWN_TRANSPORT);
        this.fsm.onConnectingFailed();
    }
    private onSocketDisconnect(rawReason: string): void {
        const sessionLengthMs = this.connectionTracker.getSessionLength() || 0;
        const reason = DisconnectReason.fromRawReason(rawReason);

        this.logger.error(`onSocketDisconnect, reason: ${reason.reason}`);

        if (reason.reason === 'dup' && this.lastDisconnectReason === 'dup' &&
            sessionLengthMs < this.options.duplicateDisconnectThresholdMs) {
            this.logger.warn(
                'Socket was closed by server as Duplicate for the second time in a row ' +
                `after ${sessionLengthMs} ms which is below the threshold of ` +
                `${this.options.duplicateDisconnectThresholdMs} ms. Resetting cached ` +
                'connection parameters and making a new allocation.');
            this.reconnectParams = undefined;
        }
        this.lastDisconnectReason = reason.reason;

        this.fsm.onSocketDisconnect(reason);
        this.connectionExpireTimestampInSecs = undefined;
    }

    private onSocketReconnect(): void {
        // this callback should not be called, because reconnect on socket.io is disabled, but
        // leave it here in case error in socket.io
        this.logger.error('onSocketReconnect');
        this.fsm.onTrouterConnected();
    }
    private onSocketReconnectFailed(reason: unknown): void {
        // this callback should not be called, because reconnect on socket.io is disabled, but
        // leave it here in case error in socket.io
        this.logger.error(`onSocketReconnectFailed with '${reason}'`);
        this.fsm.onSocketDisconnect(DisconnectReason.fromSocketIoEventData('reconnecterror', reason));
    }
    private onSocketReconnecting(): void {
        // this callback should not be called, because reconnect on socket.io is disabled, but
        // leave it here in case error in socket.io
        this.logger.error('onSocketReconnecting');
    }
    private onSocketError(errorObject: unknown): void {
        this.logger.error(`onSocketError with '${toJson(errorObject)}'`);
        if (this.fsm.isConnecting()) {
            this.connectingErrorsInRow++;
            if (this.connectingErrorsInRow >= this.connectingErrorsThreshold) {
                this.resetReconnectParamsOnErrorThreshold();
            }
        }
        this.connectionTracker.trackError('connectSocket', safeToStringOrType(errorObject));
        this.fsm.onSocketDisconnect(DisconnectReason.fromSocketIoEventData('socketerror', errorObject));
    }
    private onSocketMessage(requestJson: string): void {
        this.logger.debug('onSocketMessage');

        let request: TrouterRequest;
        try {
            request = new TrouterRequest(requestJson, this.logger);
            const chainId = request.headers?.['X-Microsoft-Skype-Chain-ID'];
            const chainIdLogText = chainId ? ` Chain-Id ${chainId}` : '';
            this.logger.info(`Received request N ${request.id}${chainIdLogText} CV ${request.correlationVector} to '${request.url}'`);
            request.startTS = Date.now();

            if (request.url && this.urlPath && request.url.startsWith(this.urlPath)) {
                request.shortUrl = request.url.substring(this.urlPath.length);
            }
        } catch (error) {
            const msg = errorStackToJson(error);
            this.logger.error(`unable to parse request. Error: ${msg}`);
            this.connectionTracker.trackRequest(undefined, msg);
            this.connectionTracker.sendResponseError(`unable to parse request, error: ${error}`);

            return;
        }

        request.timeoutTimerId = setTimeout(() => {
            if (request.replied) {
                return;
            }
            this.logger.error(`Request ${request.id} timed out`);
            const responseData = new ResponseData(request.id);
            responseData.status = 504;
            responseData.headers = { 'Trouter-Responder': 'ClientLib' };
            this.sendResponse(request, responseData);
            request.timedout = true;
        }, this.timeoutOptions.requestTimeoutMs);

        try {
            this.connectionTracker.trackRequest(request);
            this.fsm.onDownstreamRequest(request);
        } catch (error) {
            this.logger.error(`exception in socket.on message. Error: ${errorStackToJson(error)}`);
            this.connectionTracker.sendResponseError(errorMessageToString(error), request, undefined);
        }
    }
    private onTrouterConnected(
        eventArgs: ITrouterConnectedEventArgs | ITrouterConnectedEventArgsV4c,
        userActivitySnapshot?: UserActivitySnapshot,
        audienceSubscriptionSnapshot?: AudienceSubscriptionSnapshot
    ): void {
        if (isV4ConnectEvent(eventArgs)) {
            const v4cArgs = <ITrouterConnectedEventArgsV4c>eventArgs;
            this.allocateResult = { ...v4cArgs, ttl: eventArgs.ttl.toString() };
            this.populateAndCacheReconnectParams(v4cArgs, v4cArgs.reconnectUrl);
            this.populateConnectionStateFields(v4cArgs);
        } else if (!this.allocateResult) {
            this.logger.error('Invalid internal state - received onTrouterConnected while allocateResult is not set');

            return;
        }

        this.connectingErrorsInRow = 0;
        this.logger.info(`onTrouterConnected: ${this.allocateResult.url}`);

        if (this.transportTypeName === 'xhr-polling' && userActivitySnapshot) {
            this.manager.onUserActivityStateAccepted(userActivitySnapshot.cv);
        }

        // Prevent next 'trouter.connected' responses from other trouter nodes
        // hackish way, since socket.io doesn't have API for that
        if (this.socket?.socket?.options?.query) {
            this.socket.socket.options.query += '&connected=true';
        }

        // Strip off the first part of the URL, up to the path
        // eslint-disable-next-line no-useless-escape
        this.urlPath = this.allocateResult.url.replace(/https?:\/\/([A-z0-9\:\$\-\_\.\+\!\*\"\(\)\,]*)\//, '/');

        const isNewUrl = (this.connectedUrl !== this.allocateResult.url);
        this.connectedUrl = this.allocateResult.url;
        this.connectionExpireTimestampInSecs = calculateExpireTsInSec(ensureNumber(eventArgs.ttl));

        this.connectionTracker.trackEnd('connectSocket');
        this.connectionTracker.trackConnected(isNewUrl, this.transportTypeName ? this.transportTypeName : this.UNKNOWN_TRANSPORT);

        this.setAudienceSubscriptionsUnsafeAsync(audienceSubscriptionSnapshot)?.catch(reason => this.logger.error(`Re-subscribe to audiences has failed with ${reason}`));

        this.fsm.onTrouterConnected();
    }

    private onTrouterReconnect(eventArgs: ITrouterReconnectEventArgs): void {
        const target = eventArgs.target;

        this.logger.info(`onTrouterReconnect target: ${target}`);

        if (target === 'self') {
            this.fsm.onReconnectRequired(true, ReconnectReason.ServerInitiated);
        } else {
            this.fsm.onReconnectRequired(false, ReconnectReason.ServerInitiated, eventArgs);
        }
    }

    private onTrouterMessageLoss(eventArgs: ITrouterMessageLossEventArgs): void {
        this.logger.debug('onTrouterMessageLoss');

        // Send only array of tags for further processing
        this.fsm.onTrouterMessageLost(eventArgs.droppedIndicators);
    }

    private onAudienceSubscriptionResult(eventArgs: IAudienceSubscriptionsResponse): void {
        this.logger.info('onAudienceSubscriptionResult');

        if (!this.pendingAudienceSubscription) {
            return;
        }

        clearTimeout(this.pendingAudienceSubscription.timeoutId);
        this.pendingAudienceSubscription.audienceSetResolve(eventArgs);
        this.pendingAudienceSubscription = undefined;
    }

    ////////// Other functions //////////

    private buildV4cUrlParams(): string {
        const urlParams: Record<string, string | number> = {};

        urlParams.tc = encodeURI(toJson(this.clientInfo));
        urlParams.timeout = Math.floor(this.timeoutOptions.pingTimeoutMs / 1000);

        if (this.options.endpointId) {
            urlParams.epid = this.options.endpointId;
        }

        return this.appendConnectedClientIds(this.buildQuery(urlParams), true);
    }

    private attachSocketIoHandlers(socket: ISocketIo, token: TokenResult, userActivitySnapshot?: UserActivitySnapshot, audienceSubscriptionSnapshot?: AudienceSubscriptionSnapshot) {
        socket.on('connecting', (transportTypeName: string) => { this.onSocketConnecting(transportTypeName); });
        socket.on('connect', () => { this.onSocketConnect(token); });
        socket.on('connect_failed', (reason: string) => { this.onSocketConnectFailed(reason); });
        socket.on('close_during_connecting', (reason: string) => { this.onSocketConnectFailed(reason); });
        socket.on('disconnect', (reason: string) => { this.onSocketDisconnect(reason); });
        socket.on('reconnect', () => { this.onSocketReconnect(); });
        socket.on('reconnect_failed', (reason: unknown) => { this.onSocketReconnectFailed(reason); });
        socket.on('reconnecting', () => { this.onSocketReconnecting(); });
        socket.on('error', (error: unknown) => { this.onSocketError(error); });
        socket.on('message', (request: string) => { this.onSocketMessage(request); });
        socket.on('trouter.connected',
            (eventArgs: ITrouterConnectedEventArgs) => { this.onTrouterConnected(eventArgs, userActivitySnapshot, audienceSubscriptionSnapshot); });
        socket.on('trouter.reconnect',
            (eventArgs: ITrouterReconnectEventArgs) => { this.onTrouterReconnect(eventArgs); });
        socket.on('trouter.message_loss',
            (eventArgs: ITrouterMessageLossEventArgs) => { this.onTrouterMessageLoss(eventArgs); });

        // event valid only for long polling
        // when user subscribes for audience events a query param `audienceSubscriptionState` is added to all long poll GETs 
        // if the audience subscription state differs from the one defined on server, it will adjust its state to be identical
        // after audience set opeartion finished server side `audience.subscriptionresult` is fired marking the end of asynchronous operation
        socket.on('audience.subscriptionresult',
            (_: string, eventArgs: IAudienceSubscriptionsResponse) => { this.onAudienceSubscriptionResult(eventArgs); });
    }

    private onNavigatorOnlineStatusUpdate() {
        const state = window.navigator.onLine;
        this.logger.debug(`Browser online status update - new state: ${state}, previously: ${this.isNavigatorOnline}`);
        if (state && !this.isNavigatorOnline) {
            this.isNavigatorOnline = true;
            this.tokenBackoff.expediteIfPending();
            this.connectionTracker.trackProgress('browserNet', 'online');
        } else if (!state && this.isNavigatorOnline) {
            this.isNavigatorOnline = false;
            this.connectionTracker.trackProgress('browserNet', 'offline');
        }
    }

    private onAllocationResponse(allocateResult: IAllocateResult, token: TokenResult) {
        this.logger.info(`Received allocation response ${JSON.stringify(allocateResult)}`);
        this.allocateResult = allocateResult;
        this.populateAndCacheReconnectParams(this.allocateResult, `${this.allocateResult.socketio}v4/a`);
        this.populateConnectionStateFields(this.allocateResult);
        this.fsm.onAllocationSucceed(token);
    }

    private populateAndCacheReconnectParams(args: ITrouterConnectedEventArgsV4c | IAllocateResult, reconnectUrl: string) {
        this.reconnectParams = {
            serviceUrl: this.options.trouterUrl,
            reconnectUrl,
            ...args.connectparams
        };
        this.manager.onConnectionParametersUpdated(this.reconnectParams);
    }

    private populateConnectionStateFields(args: IAllocateResult | ITrouterConnectedEventArgsV4c) {
        const ttl: number = typeof args.ttl === 'string' ? parseInt(args.ttl, 10) : args.ttl;
        // Don't use allocateResult.connectparams.se here since system clock can be
        // incorrect on client's machine, so using TTL is safer
        this.connectionExpireTimestampInSecs = calculateExpireTsInSec(ttl);
        this.connectionId = args.id ?? '';

        this.connectedClientId = args.ccid;
        this.logger.debug(`connected client id set {connectedClientId:${this.connectedClientId}}`);

        this.c2cUrlBase = args.curlb ?? '';
        if (this.c2cUrlBase === '') {
            // Try to just remove the port as a fallback if the service didn't provide anything
            let pos = args.surl.indexOf('://');
            if (pos >= 0) {
                pos = args.surl.indexOf('/', pos + 3);
                if (pos >= 5 && args.surl.substr(pos - 5, 5) === ':3443') {
                    this.c2cUrlBase = args.surl.substr(0, pos - 5);
                }
            }
        }
    }

    private onPingResponse() {
        this.logger.debug('onPingResponse');
        this.connectionTracker.increasePingResponseCount();
        this.clearPingResponseTimer();
        this.fsm.onPingResponse();
    }

    private clearPingResponseTimer() {
        if (this.pingResponseTimerId !== undefined) {
            clearTimeout(this.pingResponseTimerId);
            this.pingResponseTimerId = undefined;
        }
    }

    private buildQuery(map: Record<string, unknown>): string {
        // TO-DO: Replace this and all other similar functions around with URLSearchParams
        const components: string[] = [];
        for (const i of Object.keys(map)) {
            if (map[i] !== undefined) {
                components.push(`${i}=${map[i]}`);
            }
        }

        return components.join('&');
    }

    private appendConnectedClientIds(url: string, isParamsOnly: boolean) {
        let params = '';
        if (!url.includes('ccid=')) {
            params = `ccid=${this.connectedClientId}&`;
        }
        if (this.domId) {
            params += `dom=${this.domId}&`;
        }
        if (params.length > 0) {
            params = params.slice(0, -1);
        }
        const sep = (isParamsOnly || url.includes('?')) ? ('&') : ('?');

        return this.appendCorrelationIds(url + sep + params, isParamsOnly);
    }

    private appendEndpointId(url: string, isParamsOnly: boolean): string {
        const sep = (isParamsOnly || url.includes('?')) ? ('&') : ('?');

        return (!url.includes('epid') && this.options.endpointId)
            ? `${url}${sep}epid=${this.options.endpointId}`
            : url;
    }

    private appendCorrelationIds(url: string, isParamsOnly: boolean): string {
        const sep = (isParamsOnly || url.includes('?')) ? ('&') : ('?');

        return (!url.includes('cor_id'))
            ? `${url}${sep}cor_id=${this.options.clientCorrelationID}`
            + `&con_num=${this.clientID}_${this.connectionAttempt}`
            : url;
    }

    private safeString(str: unknown): string {
        return (typeof str === 'string') ? str : '';
    }

    private sendResponse(request: TrouterRequest, responseData: ResponseData): SendResponseResult {
        if (request.timedout) {
            this.logger.error(`Request ${request.id} already timed out`);

            return SendResponseResult.Timeout;
        }
        if (request.replied) {
            this.logger.error(`Response for request ${request.id} already sent`);

            return SendResponseResult.Duplicate;
        }
        clearTimeout(request.timeoutTimerId);
        request.timeoutTimerId = undefined;
        request.replied = true;

        responseData.headers = responseData.headers ?? {};

        const cv = request.correlationVector;
        this.logger.info(`Sending response N ${request.id} CV ${cv} with status ${responseData.status}`);
        if (cv) {
            responseData.headers[cvHeader] = cv;
        }

        // Echo back the incoming "trouter-request" header
        if (request.headers?.['trouter-request']) {
            responseData.headers['trouter-request'] = request.headers['trouter-request'];
        }
        // Fill in "trouter-client" header
        const latencyMS = Date.now() - request.startTS;
        responseData.headers['trouter-client'] = toJson({
            cd: latencyMS
        });

        // Echo back "trouter-is-broadcast" header
        if (request.headers?.['trouter-is-broadcast']) {
            responseData.headers['trouter-is-broadcast'] = request.headers['trouter-is-broadcast'];
        }

        this.logger.debug(`response: ${toJson(responseData)}`);

        if (this.socket) {
            try {
                this.socket.send(toJson(responseData));
                responseData.sentTS = Date.now();
                request.incrementCorrelationVector();
                this.connectionTracker.trackResponse(request, latencyMS, responseData);
                if (this.transportTypeName === 'websocket') {
                    // Send ping request immediately to emulate 'ack' from server, so
                    // client can faster react on network issues
                    this.sendPingRequest();
                }

                return SendResponseResult.OK;
            } catch (error) {
                const errorMsg = `unable to send data on response.end. Error: ${errorStackToJson(error)}`;
                this.logger.error(errorMsg);
                this.connectionTracker.sendResponseError(errorMsg, request, responseData);

                return SendResponseResult.Disconnected;
            }
        } else {
            this.connectionTracker.sendResponseError('no socket', request, responseData);

            return SendResponseResult.Disconnected;
        }
    }

    /*
        Sends the activity state event to Trouter.
        Has implemented support for sending the current state twice (or multiple times)
        after reconnect, first immediately, and theen the second after configured delay.

        If after the first sent state event the client app changes the state,
        the new state is sent in the second event after the delay.
        Meanwhile the new state is also sent immediately upon
        the change, but the planned second sending (after delay) is triggered anyway,
        to fulfill it's purpose, which is to prevent server-side race condition.
     */
    private sendUserActivityStateMultiple(remaining: number): void {
        const activityEvent = new DownstreamEvent('user.activity');
        const activityEventObject = this.userActivityState.increaseCvAndGetEventObject();
        activityEvent.args = activityEventObject;

        this.logger.debug(`Sending user activity '${this.userActivityState.toEventJSON()}', remaining ${remaining - 1}`);
        let timeoutReached = false;
        this.sendDownstreamEvent(activityEvent, () => {
            if (timeoutReached === true) {
                return;
            }
            this.logger.info(`User activity state: ${activityEventObject.state}, cv: ${activityEventObject.cv} accepted`);
            this.manager.onUserActivityStateAccepted(activityEventObject.cv);
            this.clearSentEventTimer(activityEvent.timeoutTimerId);
            if (remaining > 1) {
                const timerId = setTimeout(() => {
                    this.clearSentEventTimer(timerId);
                    this.sendUserActivityStateMultiple(remaining - 1);
                }, this.options.userActivitySecondResendDelayMs);
                this.registerSentEventTimer(timerId, 'user.activity/resend');
            }
        });

        activityEvent.timeoutTimerId = setTimeout(() => {
            this.logger.error('Activity state response timeout is fired');
            timeoutReached = true;
            this.fsm.onActivityStateResponseTimeout();
            this.clearSentEventTimer(activityEvent.timeoutTimerId);
        }, this.timeoutOptions.userActivityResponseTimeoutMs);
        this.registerSentEventTimer(activityEvent.timeoutTimerId, 'user.activity/response');
    }

    private sendDownstreamEvent(event: DownstreamEvent, responseCallback: (name: string, args?: unknown) => void) {
        this.logger.debug(`Sending downstream event ${event.name}`);

        if (this.socket) {
            this.socket.emit(event.name, event.args, responseCallback);
        }
    }

    private registerSentEventTimer(timerId: Timeout, timerDescription: string): void {
        this.logger.debug(`registering timer ${timerId} -> ${timerDescription}`);
        this.pendingSentEventTimers[<number><unknown>timerId] = timerDescription;
    }

    private clearSentEventTimer(id: Timeout): void {
        const timerDescription: string = this.pendingSentEventTimers[<number><unknown>id];
        this.logger.debug(`clearing timer ${id} -> ${timerDescription}`);
        delete this.pendingSentEventTimers[<number><unknown>id];
        clearTimeout(id);
    }

    /**
     * Calculate registration interval. Result is based on configuration parameters
     * and allocation ttl value from trouter server.
     * Returns true only if the registration ttl is present and is smaller than known `allocationTtl`.
     * This means we'll need to re-register to keep the registration alive for the duration of the allocation.
     */
    private getRegistrationTtl(): [number, boolean] {
        const allocationTtl = calculateTtlInSec(this.connectionExpireTimestampInSecs);
        this.logger.debug(`Current connectionID will expire in ${allocationTtl} seconds`);

        if (this.options.registration?.registrarTtlSec && allocationTtl > 0) {
            const isLower: boolean = this.options.registration.registrarTtlSec < allocationTtl;

            return [Math.min(this.options.registration.registrarTtlSec, allocationTtl), isLower];
        } else if (this.options.registration?.registrarTtlSec) {
            return [this.options.registration.registrarTtlSec, false];
        } else if (allocationTtl > 0) {
            return [allocationTtl, false];
        }

        return [DEFAULT_REGISTRATION_TIMEOUT_SEC, false];
    }

    private clearIncallModeTimerId() {
        if (this.incallModeTimerId !== undefined) {
            this.logger.debug('Clearing in-call mode timer');
            clearTimeout(this.incallModeTimerId);
            this.incallModeTimerId = undefined;
        }
    }

    private applyConnectionTrackerOptions(options: ITrouterOptions) {
        try {
            const eventLoggerSent = (options.eventLogger && typeof options.eventLogger.logEvent === 'function');
            if (!eventLoggerSent) {
                this.logger.warn('Trouter client event logging disabled due to invalid configuration.');
            } else {
                this.connectionTracker.mergeSettings(options.telemetrySettings);
                this.connectionTracker.enable(options.eventLogger);
            }
        } catch (error) {
            this.logger.warn(`Trouter client event logging disabled. Error: ${errorStackToJson(error)}`);
            this.connectionTracker.disable();
        }
    }

    /**
     * Returns boolean indicating if we can retry token fetch
     * based on value of optional configuration 'retryLimitOnTokenFetch'
     * @returns boolean
     */
    private canRetryTokenFetchRequest(currentCounter: number) {
        const retryLimit = this.options.retryLimitOnTokenFetch;

        if (retryLimit === null || retryLimit === undefined) {

            return true;
        }

        if (currentCounter < retryLimit) {

            return true;
        }

        this.logger.warn(
            `Reached limit on maximum number of token fetch request. Current count: ${currentCounter}, retry limit: ${retryLimit}`
        );

        return false;
    }
}

function headersFromToken(token: TokenResult, usingLegacyTokenApi: boolean): Record<string, string> {
    const migrationHeader = usingLegacyTokenApi ? undefined : { 'X-MS-Migration': 'True' };
    switch (token.tokenType.toLowerCase()) {
        case 'skype':
            return { 'X-Skypetoken': token.token, ...migrationHeader };

        case 'aad':
        case 'cae':
            return { Authorization: `Bearer ${token.token}`, ...migrationHeader };

        default:
            throw new Error(`unsupported token type: ${token.tokenType}`);
    }
}

function safeToStringOrType(value: unknown): string {
    if (value !== undefined && value !== null && typeof value.toString === 'function') {
        return value.toString();
    }
    return `[${typeof value}]`;
}

function errorStackToJson(error: unknown): string {
    if (typeof error === 'object' && error !== null && (<Record<string, unknown>>error).stack !== undefined) {
        // Keeping this as it was for now, but `error.stack` is usually just a string anyway.
        // I guess it also converts newlines to '\n', which some part may need? Or it's completely unnecessary?
        return toJson((<Record<string, unknown>>error).stack);
    }
    return '"(no error.stack)"';
}

function errorMessageToString(error: unknown): string {
    if (typeof error === 'object' && error !== null && typeof (<Record<string, unknown>>error).message === 'string') {
        return (<Record<string, string>>error).message;
    }
    return '(no error.message)';
}



// WEBPACK FOOTER //
// ./lib_temp/TrouterConnection.ts